import { Box, Button, Card, CardContent, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import { ResidentDto } from "../../dtos/ResidentDto";
import { DialogTitleClosable } from "../../components/DialogTitleClosable";
import { bhpButtonSx } from "../../styles/bhpStyles";
import { useEffect, useState } from "react";
import { CardholderCompetencyResponse, useManageCompetencyHttp } from "./useManageCompetencyHttp";
import { getTomorrow } from "../../utils";
import { LoadingButton } from "@mui/lab";
import UpdateIcon from '@mui/icons-material/Update';
import CancelIcon from '@mui/icons-material/Cancel';
import { CardholderCompetencyDto } from "../../dtos/CardholderCompetencyDto";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';


type ManageCardholderCompetencyDialogProps = {

    competency: string,
    dialogOpen : boolean;
    dialogOpenCounter : number;
    onClose: ( ) => void;
    resident: ResidentDto,

};


export const ManageCardholderCompetencyDialog : React.FC< ManageCardholderCompetencyDialogProps > = ( props : ManageCardholderCompetencyDialogProps ) => {

    const { competency, dialogOpen, dialogOpenCounter, onClose, resident } = props;

    const tomorrow: string = getTomorrow( );

    const [ cardholderCompetencyDto, setCardholderCompetencyDto ] = useState< CardholderCompetencyDto >( );
    const [ errorMessage, setErrorMessage ] = useState< string >( '' );
    const [ expiry, setExpiry ] = useState< string >( tomorrow );
    const [ noExpiry, setNoExpiry ] = useState< boolean >( false );
    const [ isActive, setIsActive ] = useState< boolean >( false );
    const [ isLoading, setIsLoading ] = useState< boolean >( false );
    const [ isUpdated, setIsUpdated ] = useState< boolean >( false );
    const [ isUpdating, setIsUpdating ] = useState< boolean >( false );

    const { addCardholderCompetency, getCardholderCompetency, removeCardholderCompetency } = useManageCompetencyHttp( );


    useEffect( () => {

        const getCompetency = async ( ) => {

            setErrorMessage( '' );

            setIsLoading( true );
            const cardholderCompetency: CardholderCompetencyResponse = await getCardholderCompetency( competency, resident.employeeId, "MMV" ); 
            setIsLoading( false );

            if ( cardholderCompetency.cardholderCompetencyDto ) {
    
                setCardholderCompetencyDto( cardholderCompetency.cardholderCompetencyDto );
                setIsActive( cardholderCompetency.cardholderCompetencyDto.isActive );
                
                if( cardholderCompetency.cardholderCompetencyDto.expiry ){
                
                    setExpiry( cardholderCompetency.cardholderCompetencyDto.expiry );
                    setNoExpiry( false );

                } else {

                    setExpiry( tomorrow );
                    setNoExpiry( true );

                }

            } else {

                setCardholderCompetencyDto( undefined );

                if( cardholderCompetency.statusCode === 404 ) {

                    setErrorMessage( 'Cardholder is not registered at the server' );

                } else {

                    setErrorMessage( 'Error getting data from the server' );

                }

            }

        };

        getCompetency( );

    }, [ dialogOpenCounter ] );


    useEffect( ( ) => {

        if( !cardholderCompetencyDto ){
            
            return;

        }
        
        if( cardholderCompetencyDto.isActive !== isActive 
            || cardholderCompetencyDto.expiry !== expiry 
            || ( noExpiry && Boolean( cardholderCompetencyDto.expiry ) )

        ) {

            setIsUpdated( true );

        } else {

            setIsUpdated( false );

        }

    },[ isActive, expiry, noExpiry ]);


    const handleCancelClick = ( ) => { 
        
        if( isUpdating ) {

            return;

        }

        onClose( );
    
    };


    const handleIsActiveChange = ( checked: boolean ) => { 

        if( checked ){

            setExpiry( tomorrow );

        }

        setIsActive( checked );

    };


    const handleExpiryChange = ( expiry: string ) => setExpiry( expiry );


    const handleNoExpiryChange = ( checked: boolean ) => setNoExpiry( checked );


    const handleUpdateClick = async () => {

        setIsUpdating( true );

        if( isActive ){

            await addCardholderCompetency( competency, resident.employeeId, noExpiry ? undefined : expiry, "MMV" );

        } else {

            await removeCardholderCompetency( competency, resident.employeeId, "MMV" );

        }

        setIsUpdating( false );

        onClose( );

    };

    
    return(

        <Dialog
            sx={{ '& .MuiDialog-paper': { width: '700px' } }}
            open={ dialogOpen }
        >
            
            <DialogTitleClosable onClose={ handleCancelClick }>
                Manage { competency } Competency
            </DialogTitleClosable>

            <DialogContent dividers>

                { 
                    errorMessage !== ''
                        &&
                    <Stack display="flex" alignItems="center">
                        <WarningAmberIcon sx={{ color: '#e65400', fontSize: '100px' }} />
                        <Typography>
                            { errorMessage }
                        </Typography>
                    </Stack>
                }

                {
                    isLoading
                        ?
                    <Box 
                        display="flex" 
                        alignItems="center"
                        justifyContent="center"
                        minHeight={ 100 }
                    >
                        <CircularProgress size="80px" sx={{color: '#e65400'}}/>
                    </Box>
                        :
                    <>
                        { 
                            errorMessage === ''
                                &&
                            <Stack>

                                <FormControlLabel control={
                                    <Checkbox checked={ isActive } onChange={( _, checked: boolean ) => handleIsActiveChange( checked ) }/>
                                } label="Is Active" />

                                <FormControlLabel control={
                                    <Checkbox disabled={ !isActive } checked={ noExpiry } onChange={( _, checked: boolean ) => handleNoExpiryChange( checked ) }/>
                                } label="No Expiry" />

                                <TextField
                                    value={ expiry?.substring( 0, 10 ) }
                                    disabled={ !isActive || noExpiry }
                                    onChange={ ( event: React.ChangeEvent<HTMLInputElement> ) => handleExpiryChange( event.target.value ) }
                                    onKeyDown={ e => e.preventDefault()}
                                    label="Expiry"
                                    type="date"
                                    sx={{ marginTop: '10px', width: '400px' }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    inputProps={{
                                        min: tomorrow,
                                    }}
                                />

                            </Stack>
                        }
                    </>

                }
 
            </DialogContent>

            <DialogActions>

                <LoadingButton
                    disabled={ isLoading || !isUpdated }
                    loading={ isUpdating }
                    loadingPosition="start"
                    onClick={ handleUpdateClick } 
                    startIcon={ <UpdateIcon /> }
                    sx={{ ...bhpButtonSx, width: '120px' }}
                    variant="contained"
                >
                    Update
                </LoadingButton>

                <Button
                    disabled={ isLoading || isUpdating }
                    variant="contained"
                    sx={{ ...bhpButtonSx, width: '120px' }}
                    onClick={() => handleCancelClick( )}
                >
                    <CancelIcon sx={{ mr: 1 }} />
                    Cancel
                </Button>

            </DialogActions>
        </Dialog>

    );

};